import { partition } from 'lodash';
import keyBy from 'lodash/fp/keyBy';

import { Label, LabelsByBoards } from '../../../../../../../common/labels/labelTypes';
import { RECENT_LABELS_SECTION_ID } from './labelPopupUtils';

/**
 * Filters out labels that are no longer available and refreshes the data to reflect any updates to the label (e.g. name or color changes)
 */
const filterAndRefreshLabels = (labels: Label[], allLabelsById: Record<string, Label>): Label[] =>
    labels.filter((label) => allLabelsById[label._id]).map((label) => allLabelsById[label._id]);

const refreshSectionLabels = (labelsByBoards: LabelsByBoards, allLabelsById: Record<string, Label>): LabelsByBoards =>
    labelsByBoards
        .map(({ id, title, isRoot, labels }) => ({
            id,
            title,
            isRoot,
            labels: filterAndRefreshLabels(labels, allLabelsById),
        }))
        .filter(({ labels }) => labels.length > 0);

const getMergedLabelIds = (labelsByBoards: LabelsByBoards): Set<string> =>
    new Set(labelsByBoards.flatMap(({ labels }) => labels.map((label) => label._id)));

const appendMissingLabelsFromLatestSections = (
    mergedLabelsByBoards: LabelsByBoards,
    latestLabelsByBoards: LabelsByBoards,
    allLabelsById: Record<string, Label>,
): LabelsByBoards => {
    const mergedLabelIds = getMergedLabelIds(mergedLabelsByBoards);

    latestLabelsByBoards.forEach(({ id, title, isRoot, labels }) => {
        const mergedGroup = mergedLabelsByBoards.find((group) => group.id === id);
        if (mergedGroup) {
            mergedGroup.title = title;
            mergedGroup.isRoot = isRoot;
        }

        const labelsToAdd = labels.filter((label) => allLabelsById[label._id] && !mergedLabelIds.has(label._id));

        if (!labelsToAdd.length) return;

        labelsToAdd.forEach((label) => mergedLabelIds.add(label._id));

        if (mergedGroup) {
            mergedGroup.labels = [...mergedGroup.labels, ...labelsToAdd];
            return;
        }

        mergedLabelsByBoards.push({ id, title, isRoot, labels: labelsToAdd });
    });

    return mergedLabelsByBoards;
};

const splitRecentSection = (labelsByBoards: LabelsByBoards) => {
    const [recentLabelSections, boardSuggestedLabels] = partition(
        labelsByBoards,
        ({ id }) => id === RECENT_LABELS_SECTION_ID,
    );

    return {
        recentLabelsSection: recentLabelSections[0],
        boardSuggestedLabels,
    };
};

const refreshRecentSection = (
    recentLabelsSection: LabelsByBoards[number],
    allLabelsById: Record<string, Label>,
): LabelsByBoards[number] | null => {
    const refreshedRecentLabels = filterAndRefreshLabels(recentLabelsSection.labels, allLabelsById);

    if (!refreshedRecentLabels.length) return null;

    return {
        ...recentLabelsSection,
        labels: refreshedRecentLabels,
    };
};

const excludeLabelIdsFromSections = (
    labelsByBoards: LabelsByBoards,
    idsToExclude: Set<string>,
    allLabelsById: Record<string, Label>,
): LabelsByBoards =>
    labelsByBoards
        .map(({ id, title, isRoot, labels }) => ({
            id,
            title,
            isRoot,
            labels: filterAndRefreshLabels(labels, allLabelsById).filter((label) => !idsToExclude.has(label._id)),
        }))
        .filter(({ labels }) => labels.length > 0);

/**
 * Merge the existing and latest 'current labels' lists, while ensuring that only available labels are included and that
 * new labels are added. The only difference from using the latest list directly is that labels that are no longer
 * available due to being removed from the last element will still appear in the list.
 * The allLabels list is used to refresh label data (name/color) for all labels, including preserved ones.
 */
export const mergeCurrentPathLabels = (
    existingCurrentLabels: LabelsByBoards,
    latestCurrentLabels: LabelsByBoards,
    allLabels: Label[],
): LabelsByBoards => {
    const allLabelsById = keyBy('_id', allLabels);
    const refreshedCurrentLabels = refreshSectionLabels(existingCurrentLabels, allLabelsById);

    return appendMissingLabelsFromLatestSections(refreshedCurrentLabels, latestCurrentLabels, allLabelsById);
};

/**
 * Merges the popup's persisted suggestion state with the latest board suggestions while preserving any existing recent
 * labels that were already shown when the popup opened.
 */
export const mergeSuggestedLabels = (
    existingSuggestedLabels: LabelsByBoards,
    latestCurrentLabels: LabelsByBoards,
    allLabels: Label[],
): LabelsByBoards => {
    const { recentLabelsSection, boardSuggestedLabels } = splitRecentSection(existingSuggestedLabels);
    const allLabelsById = keyBy('_id', allLabels);
    const recentLabelIds = new Set(recentLabelsSection?.labels.map((label) => label._id) ?? []);
    const currentPathLabelsWithoutRecent = excludeLabelIdsFromSections(
        latestCurrentLabels,
        recentLabelIds,
        allLabelsById,
    );
    const mergedCurrentPathLabels = mergeCurrentPathLabels(
        boardSuggestedLabels,
        currentPathLabelsWithoutRecent,
        allLabels,
    );

    if (!recentLabelsSection) return mergedCurrentPathLabels;

    const refreshedRecentSection = refreshRecentSection(recentLabelsSection, allLabelsById);

    return refreshedRecentSection ? [...mergedCurrentPathLabels, refreshedRecentSection] : mergedCurrentPathLabels;
};
